/**
 * GoalHistoryChart — snapshot history chart for the goal detail view
 * (Story 18.7, AC #7).
 *
 * Self-contained: takes a `goalId`, runs its own `useGoalSnapshots(goalId)`
 * call, and renders the snapshot series as a Recharts area of `currentValue`
 * over `snapshotDate`, with a horizontal `ReferenceLine` at the goal's
 * `targetValue` (labeled "Target"). Axis + tooltip values format per the
 * goal's unit via the shared `formatGoalValue` formatter.
 *
 * States:
 * - loading  → a pulse skeleton (the hook is fetching)
 * - empty    → "No snapshots yet" hint (series has zero points)
 * - rendered → the area chart with the target reference line
 *
 * The 18.8 detail view mounts this with a single line:
 *   <GoalHistoryChart goalId={goal.id} />
 *
 * Recharts v3 (the repo's chart dep): same composition API as the report
 * widgets (LineChartWidget precedent) — ResponsiveContainer needs a fixed-
 * height parent, which the wrapper div provides.
 */

import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { useGoalSnapshots } from "../../hooks/useGoalHistory";
import { formatGoalValue } from "../../lib/goalFormat";
import type { GoalUnitType } from "../../lib/goalPageDisplay";

interface GoalHistoryChartProps {
  goalId: string;
}

const NAVY = "#1e2a44";

/** Short UTC tick for a YYYY-MM-DD snapshot date, e.g. "Apr 12". */
function formatDateTick(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function ChartTooltip({
  active,
  payload,
  label,
  unit,
  currencyCode,
}: {
  active?: boolean;
  payload?: Array<{ value?: number }>;
  label?: string;
  unit: GoalUnitType;
  currencyCode: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const value = payload[0]?.value;
  if (typeof value !== "number") return null;
  return (
    <div className="rounded-md border border-border bg-popover p-2 text-xs shadow-md">
      <p className="mb-0.5 font-medium">{formatDateTick(label ?? "")}</p>
      <p className="text-muted-foreground">
        {formatGoalValue(value, unit, currencyCode)}
      </p>
    </div>
  );
}

export function GoalHistoryChart({ goalId }: GoalHistoryChartProps) {
  const { data, isLoading } = useGoalSnapshots(goalId);

  const points = useMemo(
    () =>
      (data?.snapshots ?? []).map((s) => ({
        date: s.snapshotDate,
        value: s.currentValue,
      })),
    [data?.snapshots],
  );

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="goal-history-chart-loading">
        <div className="h-3 w-28 animate-pulse rounded bg-muted" />
        <div className="h-40 w-full animate-pulse rounded-lg bg-muted/60" />
      </div>
    );
  }

  if (!data || points.length === 0) {
    return (
      <div className="px-4 py-6 text-center" data-testid="goal-history-chart-empty">
        <p className="text-[13px] text-muted-foreground">
          No snapshots yet history builds up as the goal is evaluated.
        </p>
      </div>
    );
  }

  const { unit, currencyCode, targetValue } = data.goal;

  return (
    <div className="space-y-2" data-testid="goal-history-chart">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
        Snapshot history
      </p>
      <div className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 12, right: 12, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id={`goal-hist-fill-${goalId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={NAVY} stopOpacity={0.25} />
                <stop offset="100%" stopColor={NAVY} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(214 25% 70% / 0.18)" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateTick}
              tick={{ fontSize: 10, fill: NAVY }}
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 10, fill: NAVY }}
              tickFormatter={(v: number) =>
                new Intl.NumberFormat("en-US", { notation: "compact" }).format(v)
              }
              tickLine={false}
              axisLine={false}
              width={44}
              tickMargin={4}
            />
            <Tooltip
              content={
                <ChartTooltip unit={unit} currencyCode={currencyCode} />
              }
            />
            <ReferenceLine
              y={targetValue}
              stroke="#dc2626"
              strokeDasharray="5 5"
              strokeWidth={1}
              ifOverflow="extendDomain"
              label={{
                value: "Target",
                position: "insideTopRight",
                fill: "#dc2626",
                fontSize: 10,
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={NAVY}
              strokeWidth={2}
              fill={`url(#goal-hist-fill-${goalId})`}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
